import React, { useState, useEffect } from 'react';
import SideNav from '../components/SideNav';
import { Upload, Film, Music, Scissors, Play, Download, Wand2, Layers, CheckCircle, Video, MonitorPlay, BarChart3, Clock, Settings } from 'lucide-react';

const StudioLab = () => {
  const [status, setStatus] = useState('IDLE'); // IDLE, PROCESSING, READY
  const [format, setFormat] = useState('SHORT'); // SHORT, LONG
  const [progress, setProgress] = useState(0);
  const [log, setLog] = useState("System Ready.");

  // FAKE AI PROCESSING SEQUENCE
  const startRender = () => {
    setStatus('PROCESSING');
    const steps = [
      { pct: 10, msg: "Million: Analyzing uploaded raw footage..." },
      { pct: 30, msg: "Million: Detecting highlights and removing silence..." },
      { pct: 50, msg: "Million: Fetching B-Roll from Library (Tags: Automotive, Shop)..." },
      { pct: 70, msg: "Million: Syncing Audio & SFX to Beat..." },
      { pct: 90, msg: "Million: Applying Color Grade & Final Polish..." },
      { pct: 100, msg: "RENDER COMPLETE. OPENING EDITOR." }
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      if (currentStep >= steps.length) {
        clearInterval(interval);
        setStatus('READY');
      } else {
        setProgress(steps[currentStep].pct);
        setLog(steps[currentStep].msg);
        currentStep++;
      }
    }, 800); // Fast simulation for reviewer
  };

  return (
    <div style={styles.layout}>
      <SideNav activePage="/studio-lab" />

      <div style={styles.mainContent}>
        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>STUDIO LAB <span style={styles.betaTag}>ALPHA PREVIEW</span></h1>
            <p style={styles.subTitle}>AI-ASSISTED VIDEO COMPOSITOR // AUTO-EDIT & B-ROLL INJECTION</p>
          </div>
          <div style={styles.statusBadge}>
            <div style={styles.dot}></div> MILLION ENGINE: {status === 'PROCESSING' ? 'ACTIVE' : 'STANDBY'}
          </div>
        </div>

        <div style={styles.gridContainer}>

          {/* LEFT: ASSET LIBRARY */}
          <div style={styles.panelLeft}>
            <div style={styles.panelHeader}><Layers size={16} color="#D4AF37" /> ASSET LIBRARY</div>

            <div style={styles.sectionTitle}>USER UPLOADS (A-ROLL)</div>
            <div style={styles.assetGrid}>
              <div style={styles.assetCard}>
                <div style={styles.assetThumb}><Video size={20} /></div>
                <div style={styles.assetName}>IMG_4022.MOV</div>
              </div>
              <div style={styles.assetCard}>
                <div style={styles.assetThumb}><Video size={20} /></div>
                <div style={styles.assetName}>IMG_4023.MOV</div>
              </div>
              <div style={styles.uploadBox}>
                <Upload size={20} color="#666" />
                <span>UPLOAD RAW</span>
              </div>
            </div>

            <div style={styles.sectionTitle}>MILLION LIBRARY (B-ROLL)</div>
            <div style={styles.assetGrid}>
              <div style={styles.assetCardBroll}>
                <div style={styles.assetThumb}><Film size={20} /></div>
                <div style={styles.assetName}>WRENCH_TURN.MP4</div>
              </div>
              <div style={styles.assetCardBroll}>
                <div style={styles.assetThumb}><Film size={20} /></div>
                <div style={styles.assetName}>SHOP_TIME_LAPSE.MP4</div>
              </div>
              <div style={styles.assetCardBroll}>
                <div style={styles.assetThumb}><Music size={20} /></div>
                <div style={styles.assetName}>ROCK_UPBEAT.MP3</div>
              </div>
            </div>
          </div>

          {/* CENTER: EDITOR / PREVIEW */}
          <div style={styles.panelCenter}>
            {status === 'IDLE' && (
              <div style={styles.emptyState}>
                <MonitorPlay size={60} color="#333" />
                <h2>AWAITING GENERATION</h2>
                <p>Select format and initialize Million to compile footage.</p>
              </div>
            )}

            {status === 'PROCESSING' && (
              <div style={styles.processingState}>
                <Wand2 size={40} color="#D4AF37" className="spin" />
                <h2 style={{ color: '#D4AF37' }}>MILLION IS EDITING...</h2>
                <div style={styles.progressBar}><div style={{ ...styles.progressFill, width: `${progress}%` }}></div></div>
                <div style={styles.terminalLog}>
                  <span style={{ color: '#D4AF37' }}>{">"}</span> {log}
                </div>
              </div>
            )}

            {status === 'READY' && (
              <div style={styles.editorUI}>
                {/* Video Player Mockup */}
                <div style={styles.playerScreen}>
                  <div style={styles.playOverlay}><Play size={40} fill="#fff" /></div>
                  <div style={styles.gradeBadge}>GRADE: A-</div>
                </div>
                {/* Timeline Mockup */}
                <div style={styles.timeline}>
                  <div style={styles.trackLabel}><Video size={12} /> VIDEO 1</div>
                  <div style={styles.trackRow}>
                    <div style={{ ...styles.clip, width: '30%', background: '#4caf50' }}>TALKING_HEAD</div>
                    <div style={{ ...styles.clip, width: '15%', background: '#2196f3' }}>B-ROLL</div>
                    <div style={{ ...styles.clip, width: '40%', background: '#4caf50' }}>TALKING_HEAD</div>
                  </div>
                  <div style={styles.trackLabel}><Music size={12} /> AUDIO</div>
                  <div style={styles.trackRow}>
                    <div style={{ ...styles.clip, width: '100%', background: '#ff9800' }}>BG_MUSIC_DRIVING</div>
                  </div>
                  <div style={styles.timeRuler}>00:00 ---------------- 00:15 ---------------- 00:30</div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: CONTROLS */}
          <div style={styles.panelRight}>
            <div style={styles.panelHeader}><Settings size={16} color="#D4AF37" /> CONFIGURATION</div>

            <div style={styles.controlGroup}>
              <label style={styles.label}>OUTPUT FORMAT</label>
              <div style={styles.toggleRow}>
                <button style={format === 'SHORT' ? styles.toggleBtnActive : styles.toggleBtn} onClick={() => setFormat('SHORT')}>SHORTS (9:16)</button>
                <button style={format === 'LONG' ? styles.toggleBtnActive : styles.toggleBtn} onClick={() => setFormat('LONG')}>LONG (16:9)</button>
              </div>
            </div>

            <div style={styles.controlGroup}>
              <label style={styles.label}>PACING</label>
              <select style={styles.select}>
                <option>FAST (TikTok Style)</option>
                <option>CINEMATIC (Documentary)</option>
                <option>EXPLAINER (Educational)</option>
              </select>
            </div>

            <div style={styles.divider}></div>

            {status !== 'READY' ? (
              <button style={styles.generateBtn} onClick={startRender}>
                <Wand2 size={18} /> INITIALIZE MILLION RENDER
              </button>
            ) : (
              <div style={styles.exportSection}>
                <div style={styles.scoreCard}>
                  <div style={styles.scoreTitle}>CONTENT SCORE</div>
                  <div style={styles.scoreValue}>92/100</div>
                  <p style={styles.scoreText}>Good hook. B-Roll sync is optimal.</p>
                </div>
                <button style={styles.exportBtn}>
                  <Download size={18} /> EXPORT TO DEVICE
                </button>
                <button style={styles.resetBtn} onClick={() => setStatus('IDLE')}>
                  NEW PROJECT
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* CSS Animation for spinner */}
      <style>{`
        .spin { animation: spin 2s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#000', color: '#fff', fontFamily: 'Inter, sans-serif' },
  mainContent: { flex: 1, padding: '30px', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #222', paddingBottom: '20px' },
  pageTitle: { fontFamily: 'Orbitron', fontSize: '2rem', color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '15px' },
  betaTag: { fontSize: '0.8rem', background: '#222', color: '#D4AF37', padding: '5px 10px', borderRadius: '4px', border: '1px solid #333' },
  subTitle: { color: '#666', margin: '5px 0 0 0', fontSize: '0.9rem', letterSpacing: '1px' },
  statusBadge: { display: 'flex', alignItems: 'center', gap: '8px', color: '#4caf50', fontSize: '0.9rem', fontFamily: 'Orbitron' },
  dot: { width: '8px', height: '8px', background: '#4caf50', borderRadius: '50%', boxShadow: '0 0 10px #4caf50' },

  gridContainer: { display: 'flex', gap: '20px', flex: 1, height: '100%' },

  // PANELS
  panelLeft: { width: '250px', background: '#0a0a0a', border: '1px solid #333', display: 'flex', flexDirection: 'column' },
  panelCenter: { flex: 1, background: '#000', border: '1px solid #333', position: 'relative', display: 'flex', flexDirection: 'column' },
  panelRight: { width: '300px', background: '#0a0a0a', border: '1px solid #333', padding: '20px' },

  panelHeader: { padding: '15px', background: '#111', borderBottom: '1px solid #333', color: '#D4AF37', fontFamily: 'Orbitron', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '10px' },

  // ASSETS
  sectionTitle: { fontSize: '0.7rem', color: '#666', padding: '15px 15px 5px 15px', fontWeight: 'bold' },
  assetGrid: { padding: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  assetCard: { background: '#1a1a1a', border: '1px solid #333', borderRadius: '4px', padding: '10px', cursor: 'pointer' },
  assetCardBroll: { background: '#111', border: '1px solid #333', borderRadius: '4px', padding: '10px', cursor: 'pointer' },
  assetThumb: { height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444', marginBottom: '5px' },
  assetName: { fontSize: '0.6rem', color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  uploadBox: { border: '1px dashed #444', borderRadius: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '75px', gap: '5px', fontSize: '0.6rem', color: '#666', cursor: 'pointer' },

  // CENTER STATES
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#444' },
  processingState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  progressBar: { width: '60%', height: '4px', background: '#222', marginTop: '20px', borderRadius: '2px' },
  progressFill: { height: '100%', background: '#D4AF37', transition: 'width 0.2s' },
  terminalLog: { marginTop: '20px', fontFamily: 'monospace', fontSize: '0.9rem', color: '#888' },

  // EDITOR UI
  editorUI: { flex: 1, display: 'flex', flexDirection: 'column' },
  playerScreen: { flex: 2, background: '#050505', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  playOverlay: { width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(212, 175, 55, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid #D4AF37' },
  gradeBadge: { position: 'absolute', top: '20px', right: '20px', background: '#4caf50', color: '#000', fontWeight: 'bold', padding: '5px 10px', borderRadius: '4px', fontFamily: 'Orbitron' },

  timeline: { flex: 1, background: '#111', borderTop: '1px solid #333', padding: '10px' },
  trackLabel: { fontSize: '0.7rem', color: '#666', marginBottom: '5px', display: 'flex', gap: '5px', alignItems: 'center' },
  trackRow: { height: '30px', background: '#000', marginBottom: '10px', display: 'flex', overflow: 'hidden' },
  clip: { height: '100%', borderRight: '1px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'rgba(255,255,255,0.8)', overflow: 'hidden' },
  timeRuler: { borderTop: '1px solid #444', fontSize: '0.6rem', color: '#444', paddingTop: '2px', fontFamily: 'monospace' },

  // CONTROLS
  controlGroup: { marginBottom: '20px' },
  label: { fontSize: '0.7rem', color: '#D4AF37', marginBottom: '10px', display: 'block', fontFamily: 'Orbitron' },
  toggleRow: { display: 'flex', gap: '10px' },
  toggleBtn: { flex: 1, background: '#111', border: '1px solid #333', color: '#666', padding: '10px', cursor: 'pointer', fontSize: '0.8rem' },
  toggleBtnActive: { flex: 1, background: 'rgba(212, 175, 55, 0.1)', border: '1px solid #D4AF37', color: '#D4AF37', padding: '10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' },
  select: { width: '100%', background: '#111', border: '1px solid #333', color: '#fff', padding: '10px', outline: 'none' },

  divider: { height: '1px', background: '#222', margin: '20px 0' },

  generateBtn: { width: '100%', background: '#D4AF37', color: '#000', border: 'none', padding: '15px', fontSize: '0.9rem', fontWeight: 'bold', fontFamily: 'Orbitron', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },

  exportSection: { display: 'flex', flexDirection: 'column', gap: '10px' },
  scoreCard: { background: '#1a1a1a', padding: '15px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #333' },
  scoreTitle: { fontSize: '0.7rem', color: '#666' },
  scoreValue: { fontSize: '1.5rem', fontFamily: 'Orbitron', color: '#4caf50' },
  scoreText: { fontSize: '0.8rem', color: '#aaa', fontStyle: 'italic' },

  exportBtn: { width: '100%', background: '#D4AF37', color: '#000', border: 'none', padding: '15px', fontSize: '0.9rem', fontWeight: 'bold', fontFamily: 'Orbitron', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  resetBtn: { width: '100%', background: 'transparent', color: '#666', border: '1px solid #333', padding: '10px', cursor: 'pointer', fontSize: '0.8rem' }
};

export default StudioLab;